/**
 * useAutodeskViewer
 *
 * Mounts Forge Viewer v7 — local SVF bundles (env: Local) or live ACC models
 * (APS Document.load with PKCE token).
 */
import { useEffect, useRef } from "preact/hooks";
import { getApsTier, isAuthDisabled } from "../auth/apsConfig";
import { getSheet } from "../data/sheets";
import { encodeViewerDocumentId } from "../services/modelDerivative/encodeUrn";
import { useAccModelStore } from "../stores/accModelStore";
import { useAccPipelineStore } from "../stores/accPipelineStore";
import { useAuthStore } from "../stores/authStore";
import { useGeometryStore } from "../stores/geometryStore";
import { useViewerStore } from "../stores/viewerStore";
import { registerGeometryOverlayExtension } from "../viewer/extensions/GeometryOverlayExtension";
import {
  ensureAccDocumentViewable,
  registerApsViewables,
} from "./useAccLiveModel";

const VIEWER_JS_URL =
  "https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js";
const VIEWER_CSS_URL =
  "https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css";

let sdkLoadPromise: Promise<typeof Autodesk> | null = null;

function loadAutodeskViewerSdk(): Promise<typeof Autodesk> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Forge Viewer SDK requires a browser"));
  }
  const w = window as unknown as { Autodesk?: typeof Autodesk };
  if (w.Autodesk?.Viewing?.Initializer) {
    return Promise.resolve(w.Autodesk);
  }
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<typeof Autodesk>((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.type = "text/css";
    css.href = VIEWER_CSS_URL;
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.src = VIEWER_JS_URL;
    script.async = true;
    script.onload = () => {
      const wAfter = window as unknown as { Autodesk?: typeof Autodesk };
      if (!wAfter.Autodesk?.Viewing?.Initializer) {
        reject(
          new Error("Forge Viewer script loaded but Autodesk global is missing")
        );
        return;
      }
      resolve(wAfter.Autodesk);
    };
    script.onerror = () =>
      reject(new Error(`Failed to load Forge Viewer from ${VIEWER_JS_URL}`));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

function apsViewerEnv(): string {
  return getApsTier() === "prd" ? "AutodeskProduction2" : "AutodeskStaging2";
}

function disposeViewer(
  viewerRef: { current: Autodesk.Viewing.GuiViewer3D | null },
  setViewer: (v: Autodesk.Viewing.GuiViewer3D | null) => void,
  setReady: (r: boolean) => void
): void {
  useGeometryStore.getState().clear();
  const v = viewerRef.current;
  if (v) {
    try {
      v.finish();
    } catch (err) {
      console.warn("[Vera] viewer.finish() threw on unmount", err);
    }
    viewerRef.current = null;
    setViewer(null);
    setReady(false);
  }
}

interface UseAutodeskViewerArgs {
  containerRef: { current: HTMLDivElement | null };
  /** When false, skip mounting Forge Viewer (ACC empty state). */
  enabled?: boolean;
}

export function useAutodeskViewer({
  containerRef,
  enabled = true,
}: UseAutodeskViewerArgs): void {
  const currentSheetId = useViewerStore(s => s.currentSheetId);
  const setViewer = useViewerStore(s => s.setViewer);
  const setReady = useViewerStore(s => s.setReady);
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const currentViewableId = useAccModelStore(s => s.currentViewableId);
  const ensureAccessToken = useAuthStore(s => s.ensureAccessToken);

  const viewerRef = useRef<Autodesk.Viewing.GuiViewer3D | null>(null);
  const docRef = useRef<Autodesk.Viewing.Document | null>(null);
  const apsLoadGeneration = useRef(0);
  const sheetSwitchReady = useRef(false);
  const lastLoadedViewableId = useRef<string | null>(null);

  function reportApsError(message: string, err?: unknown): void {
    console.error("[Vera:ACC]", message, err ?? "");
    useAccPipelineStore.getState().setError(message);
    useAccModelStore.getState().setError(message);
  }

  function loadApsViewable(
    viewer: Autodesk.Viewing.GuiViewer3D,
    doc: Autodesk.Viewing.Document,
    viewableId: string | null,
    cancelled: () => boolean,
    sheetSwitch = false
  ): void {
    void ensureAccDocumentViewable(doc, viewableId).then(node => {
      if (cancelled()) return;
      if (!node) {
        reportApsError(
          "No viewable sheets found in this model — translation may be incomplete."
        );
        return;
      }
      console.info("[Vera:ACC] loadDocumentNode for viewable", viewableId);
      lastLoadedViewableId.current = viewableId;
      viewer
        .loadDocumentNode(doc, node)
        .then(() => {
          if (cancelled()) return;
          const onGeom = () => {
            viewer.removeEventListener(
              Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
              onGeom
            );
            if (cancelled()) return;
            console.info("[Vera:ACC] Geometry loaded — viewer ready");
            if (sheetSwitch) {
              const pipeline = useAccPipelineStore.getState();
              if (pipeline.active || pipeline.phase !== "ready") {
                useAccPipelineStore.getState().complete();
              }
            } else {
              useAccPipelineStore
                .getState()
                .setStep(
                  "loading_viewer",
                  71,
                  "Sheet loaded — preparing analysis…"
                );
            }
            setReady(true);
          };
          viewer.addEventListener(
            Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
            onGeom
          );
        })
        .catch(err => {
          reportApsError("Failed to load sheet geometry in viewer.", err);
        });
    });
  }

  // --- Local SVF path (demo / offline only — never when APS auth is on) ---
  useEffect(() => {
    if (!enabled || viewerUrn || !isAuthDisabled()) return;

    let cancelled = false;
    setReady(false);
    const container = containerRef.current;
    if (!container) return;

    let sheet: ReturnType<typeof getSheet>;
    try {
      sheet = getSheet(currentSheetId);
    } catch {
      return;
    }

    void (async () => {
      try {
        await loadAutodeskViewerSdk();
        if (cancelled) return;

        await new Promise<void>(resolve => {
          Autodesk.Viewing.Initializer({ env: "Local" }, () => resolve());
        });
        if (cancelled) return;

        const viewer = new Autodesk.Viewing.GuiViewer3D(container);
        const startCode: unknown = viewer.start(sheet.documentPath);
        if (typeof startCode === "number" && startCode > 0) {
          console.error(
            "[Vera] Forge Viewer start() failed with code",
            startCode
          );
          return;
        }

        viewerRef.current = viewer;
        docRef.current = null;
        setViewer(viewer);
        registerGeometryOverlayExtension();

        const onGeomLoaded = () => {
          if (!cancelled) setReady(true);
        };
        viewer.addEventListener(
          Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
          onGeomLoaded
        );
      } catch (err) {
        console.error("[Vera] Failed to initialise local Forge Viewer", err);
      }
    })();

    return () => {
      cancelled = true;
      disposeViewer(viewerRef, setViewer, setReady);
    };
  }, [enabled, viewerUrn, currentSheetId, containerRef, setViewer, setReady]);

  // --- APS ACC live model path ---
  useEffect(() => {
    if (!enabled || !viewerUrn) {
      docRef.current = null;
      sheetSwitchReady.current = false;
      lastLoadedViewableId.current = null;
      return;
    }

    const generation = ++apsLoadGeneration.current;
    sheetSwitchReady.current = false;
    lastLoadedViewableId.current = null;
    let cancelled = false;
    const isCancelled = () =>
      cancelled || generation !== apsLoadGeneration.current;

    setReady(false);
    const container = containerRef.current;
    if (!container) {
      console.warn(
        "[Vera:ACC] Viewer container not mounted yet — retry on next render"
      );
      return;
    }

    const stallTimer = window.setTimeout(() => {
      if (isCancelled()) return;
      const pct = useAccPipelineStore.getState().percent;
      if (pct >= 60 && pct < 70) {
        console.warn(
          "[Vera:ACC] Viewer init stalled at",
          pct,
          "% — check Network tab for viewer SDK / APS errors"
        );
      }
    }, 90_000);

    void (async () => {
      try {
        const documentId = encodeViewerDocumentId(viewerUrn);
        console.info(
          "[Vera:ACC] Initializing APS Forge Viewer",
          viewerUrn.slice(0, 48),
          "→ documentId",
          `${documentId.slice(0, 32)}…`
        );
        useAccPipelineStore
          .getState()
          .setStep("loading_viewer", 62, "Connecting to Autodesk viewer…");
        await loadAutodeskViewerSdk();
        if (isCancelled()) return;
        console.info("[Vera:ACC] Forge Viewer SDK ready");

        const token = await ensureAccessToken();
        if (isCancelled()) return;
        if (!token) throw new Error("Not signed in — cannot load ACC model.");
        console.info("[Vera:ACC] Access token obtained");

        useAccPipelineStore
          .getState()
          .setStep("loading_viewer", 65, "Initializing viewer session…");
        await new Promise<void>((resolve, reject) => {
          const initTimeout = window.setTimeout(() => {
            reject(
              new Error(
                "Forge Viewer initializer timed out after 60s — check APS env (stg/prd) matches your hub."
              )
            );
          }, 60_000);
          Autodesk.Viewing.Initializer(
            {
              env: apsViewerEnv(),
              api: "streamingV2",
              getAccessToken: onTokenReady => {
                void ensureAccessToken().then(t => {
                  if (t) onTokenReady?.(t, 3600);
                });
              },
            },
            () => {
              window.clearTimeout(initTimeout);
              resolve();
            }
          );
        });
        if (isCancelled()) return;
        console.info(
          "[Vera:ACC] Viewer initializer complete, env=",
          apsViewerEnv()
        );

        const viewer = new Autodesk.Viewing.GuiViewer3D(container);
        const startCode: unknown = viewer.start();
        if (typeof startCode === "number" && startCode > 0) {
          throw new Error(`Forge Viewer start() failed with code ${startCode}`);
        }

        viewerRef.current = viewer;
        setViewer(viewer);
        registerGeometryOverlayExtension();
        console.info("[Vera:ACC] Calling Document.load");

        useAccPipelineStore
          .getState()
          .setStep("loading_viewer", 68, "Loading ACC document…");

        Autodesk.Viewing.Document.load(
          documentId,
          doc => {
            if (isCancelled()) return;
            console.info("[Vera:ACC] Document.load succeeded");
            useAccPipelineStore
              .getState()
              .setStep("loading_viewer", 70, "Loading sheet geometry…");
            docRef.current = doc;
            registerApsViewables(doc, viewerUrn);
            const viewableId = useAccModelStore.getState().currentViewableId;
            sheetSwitchReady.current = true;
            loadApsViewable(viewer, doc, viewableId, isCancelled, false);
          },
          (code, message) => {
            reportApsError(
              `Failed to load ACC model in viewer (${code}): ${message}`
            );
          }
        );
      } catch (err) {
        reportApsError(
          err instanceof Error
            ? err.message
            : "Failed to initialise APS viewer",
          err
        );
      } finally {
        window.clearTimeout(stallTimer);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(stallTimer);
      disposeViewer(viewerRef, setViewer, setReady);
      docRef.current = null;
      sheetSwitchReady.current = false;
      lastLoadedViewableId.current = null;
    };
  }, [
    enabled,
    viewerUrn,
    containerRef,
    setViewer,
    setReady,
    ensureAccessToken,
  ]);

  // Switch APS sheet/view when the user changes the picker (not initial load).
  useEffect(() => {
    if (!viewerUrn || !currentViewableId || !sheetSwitchReady.current) return;
    if (lastLoadedViewableId.current === currentViewableId) return;
    const viewer = viewerRef.current;
    const doc = docRef.current;
    if (!viewer || !doc) return;

    let cancelled = false;
    setReady(false);

    console.info("[Vera:ACC] Switching viewable to", currentViewableId);
    loadApsViewable(viewer, doc, currentViewableId, () => cancelled, true);

    return () => {
      cancelled = true;
    };
  }, [viewerUrn, currentViewableId, setReady]);
}
